import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { UserCheck, Users, Target, CheckCircle, ArrowRight, Briefcase, Clock, Award } from 'lucide-react'
import { fadeIn, staggerContainer } from '../utils/motion'
import Button from '../compenents/Button'
import { useLanguage } from '../context/LanguageContext'
import { useTranslation } from '../utils/translations'

const Recruitment = () => {
  const { language } = useLanguage()
  const t = useTranslation(language)

  const approachPillars = [
    {
      icon: Target,
      title: t('recruitment.approach1Title'),
      description: t('recruitment.approach1Desc')
    },
    {
      icon: Users,
      title: t('recruitment.approach2Title'),
      description: t('recruitment.approach2Desc')
    },
    {
      icon: Briefcase,
      title: t('recruitment.approach3Title'),
      description: t('recruitment.approach3Desc')
    },
    {
      icon: CheckCircle,
      title: t('recruitment.approach4Title'),
      description: t('recruitment.approach4Desc')
    }
  ]

  const processSteps = [
    {
      icon: Target,
      title: t('recruitment.process1Title'),
      description: t('recruitment.process1Desc')
    },
    {
      icon: Users,
      title: t('recruitment.process2Title'),
      description: t('recruitment.process2Desc')
    },
    {
      icon: Briefcase,
      title: t('recruitment.process3Title'),
      description: t('recruitment.process3Desc')
    },
    {
      icon: CheckCircle,
      title: t('recruitment.process4Title'),
      description: t('recruitment.process4Desc')
    },
    {
      icon: Award,
      title: t('recruitment.process5Title'),
      description: t('recruitment.process5Desc')
    },
    {
      icon: Clock,
      title: t('recruitment.process6Title'),
      description: t('recruitment.process6Desc')
    }
  ]

  const sectors = [
    t('recruitment.sector1'),
    t('recruitment.sector2'),
    t('recruitment.sector3'),
    t('recruitment.sector4'),
    t('recruitment.sector5')
  ]

  return (
    <div className="pt-26">
      {/* Hero Section avec image de fond */}
      <section className="relative py-20 bg-gradient-to-br from-blue-600/90 to-blue-800/90 text-white overflow-hidden">
        {/* Image de fond */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url("images/recruitment-hero-bg.webp")',
            backgroundBlendMode: 'overlay'
          }}
        >
          {/* Overlay pour assombrir l'image et améliorer la lisibilité */}
          <div className="absolute inset-0 bg-black/40"></div>
        </div>
        
        {/* Pattern subtil */}
        <div className="absolute inset-0 opacity-10 bg-grid-pattern"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="inline-flex items-center px-6 py-3 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-8"
            >
              <UserCheck size={20} className="mr-2" />
              <span className="font-semibold">{t('recruitment.pageTitle')}</span>
            </motion.div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-heading font-bold mb-6 leading-tight">
              {t('recruitment.pageTitle')}
            </h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto leading-relaxed">
              {t('recruitment.pageSubtitle')}
            </p>

            {/* Stats rapides */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12 max-w-2xl mx-auto"
            >
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold mb-1">500+</div>
                <div className="text-blue-200 text-sm">{t('recruitment.stats.talents')}</div>
              </div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold mb-1">98%</div>
                <div className="text-blue-200 text-sm">{t('recruitment.stats.successRate')}</div>
              </div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold mb-1">15+</div>
                <div className="text-blue-200 text-sm">{t('recruitment.stats.yearsExp')}</div>
              </div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold mb-1">5</div>
                <div className="text-blue-200 text-sm">{t('recruitment.stats.sectors')}</div>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Vague décorative en bas */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="w-full h-12">
            <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" opacity=".25" className="fill-white"></path>
            <path d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" opacity=".5" className="fill-white"></path>
            <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z" className="fill-white"></path>
          </svg>
        </div>
      </section>

      {/* Section Approche - 4 Piliers */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-gray-900 mb-4">
              {t('recruitment.approachTitle')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('recruitment.approachSubtitle')}
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {approachPillars.map((pillar, index) => (
              <motion.div
                key={index}
                variants={fadeIn('up', index * 0.1)}
                className="bg-gray-50 rounded-2xl p-8 text-center hover:shadow-lg transition-all duration-300 group"
              >
                <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:bg-blue-700 transition-colors duration-300">
                  <pillar.icon size={28} className="text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {pillar.title}
                </h3>
                <p className="text-gray-600">
                  {pillar.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Section Image au milieu avec Processus */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="text-4xl md:text-5xl font-heading font-bold text-gray-900">
                {t('recruitment.processTitle')}
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                {t('recruitment.processSubtitle')}
              </p>
              
              <div className="space-y-4">
                {processSteps.map((step, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start space-x-4 p-4 bg-white rounded-xl shadow-sm"
                  >
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <step.icon size={24} className="text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">{step.title}</h3>
                      <p className="text-gray-600">{step.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Image au milieu */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="images/recruitment-middle.webp"
                  alt="Recrutement industriel"
                  className="w-full h-[600px] object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
              </div>
              
              {/* Badge flottant */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5 }}
                className="absolute -bottom-6 -right-6 bg-white rounded-2xl p-6 shadow-xl"
              >
                <div className="flex items-center space-x-3">
                  <Award className="w-8 h-8 text-blue-600" />
                  <div>
                    <div className="font-bold text-gray-900">{t('recruitment.floatingBadge.title')}</div>
                    <div className="text-sm text-gray-600">{t('recruitment.floatingBadge.subtitle')}</div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section Secteurs d'Intervention */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-gray-900 mb-4">
              {t('recruitment.sectorsTitle')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('recruitment.sectorsSubtitle')}
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto"
          >
            {sectors.map((sector, index) => (
              <motion.div
                key={index}
                variants={fadeIn('up', index * 0.1)}
                className="bg-blue-50 rounded-xl p-6 text-center hover:bg-blue-100 transition-colors duration-300"
              >
                <CheckCircle size={24} className="text-blue-600 mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900">{sector}</h3>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-heading font-bold mb-6">
              {t('recruitment.closingTitle')}
            </h2>
            <p className="text-xl mb-8 leading-relaxed">
              {t('recruitment.closingText')}
            </p>
            <Link to="/contact">
              <Button variant="secondary" size="lg">
                {t('recruitment.ctaButton')}
                <ArrowRight size={20} className="ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default Recruitment